/**
 * Promo codes — the whole `tier_promo_codes` table, plus who redeemed each one.
 *
 * Two things this page is careful about, both mirroring the API:
 *
 *  - "Delete" retires a code (is_active = false). A hard delete would cascade
 *    away the redemption rows, i.e. the record of who used it, which is exactly
 *    what this page exists to show.
 *  - "Uses" and "Redemptions" are different numbers and are shown separately.
 *    use_count is a lifetime counter on the code; redemption rows disappear
 *    with their user. Uses drifting above redemptions is expected, not a bug —
 *    do not add anything here that "reconciles" them.
 */
import { useCallback, useEffect, useState } from "react";
import { Ban, Check, Loader2, Pencil, Plus, Search, Tag, Users, X } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface PromoCode {
  id: string;
  code: string;
  tier_id: number;
  tier_name: string | null;
  access_days: number;
  is_active: boolean;
  max_uses: number | null;
  use_count: number;
  redemption_count: number;
  created_at: string;
  updated_at: string;
}

interface Redemption {
  id: string;
  redeemed_at: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  user_status: string | null;
  /** The user's CURRENT plan expiry, not a snapshot of what the promo granted —
   *  a buyer may have upgraded or paid since. Labelled accordingly in the UI. */
  expires_at: string | null;
  subscription_active: boolean | null;
  current_tier_id: number | null;
  current_tier_name: string | null;
}

interface TierOption {
  id: number;
  name: string;
  is_selectable: boolean;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", {
  month: "short", day: "numeric", year: "numeric",
});

/** Expiry, plus whether it has already passed — an expired date is usually the
 *  thing someone is scanning this list for. */
function expiryLabel(iso: string | null): { text: string; expired: boolean } {
  if (!iso) return { text: "\u2014", expired: false };
  return { text: fmtDate(iso), expired: new Date(iso).getTime() < Date.now() };
}

type FormState = {
  id: string | null;
  code: string;
  tier_id: number | "";
  access_days: number;
  max_uses: number | "";
  unlimited: boolean;
  is_active: boolean;
};

const EMPTY_FORM: FormState = {
  id: null, code: "", tier_id: "", access_days: 30, max_uses: 1, unlimited: false, is_active: true,
};

export default function SuperAdminPromoCodesPage() {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [tiers, setTiers] = useState<TierOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  // The code whose redemptions are open in the modal.
  const [viewing, setViewing] = useState<PromoCode | null>(null);
  const [redemptions, setRedemptions] = useState<Record<string, Redemption[]>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
      const [rows, tierRows] = await Promise.all([
        superAdminApi.get<PromoCode[]>(`/super-admin/promo-codes${qs}`),
        superAdminApi.get<TierOption[]>("/super-admin/tiers"),
      ]);
      setCodes(rows);
      setTiers(tierRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load promo codes.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { void load(); }, [load]);

  const openRedemptions = async (p: PromoCode) => {
    setViewing(p);
    if (redemptions[p.id]) return; // already fetched this session
    try {
      const rows = await superAdminApi.get<Redemption[]>(`/super-admin/promo-codes/${p.id}/redemptions`);
      setRedemptions((r) => ({ ...r, [p.id]: rows }));
    } catch {
      setRedemptions((r) => ({ ...r, [p.id]: [] }));
    }
  };

  const save = async () => {
    if (!form || saving) return;
    if (!form.code.trim() || form.tier_id === "") {
      setError("A code and a tier are both required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        tier_id: Number(form.tier_id),
        access_days: Number(form.access_days),
        is_active: form.is_active,
        ...(form.unlimited
          ? { unlimited_uses: true, max_uses: null }
          : { max_uses: Number(form.max_uses) || 1 }),
      };
      if (form.id) await superAdminApi.patch<PromoCode>(`/super-admin/promo-codes/${form.id}`, body);
      else await superAdminApi.post<PromoCode>("/super-admin/promo-codes", body);
      setForm(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the promo code.");
    } finally {
      setSaving(false);
    }
  };

  const retire = async (p: PromoCode) => {
    if (!window.confirm(
      `Retire ${p.code}?\n\nIt stops working immediately for new signups. Nothing is deleted — ` +
      `the ${p.redemption_count} redemption record${p.redemption_count === 1 ? "" : "s"} stay intact, ` +
      `and you can switch it back on by editing it.`,
    )) return;
    try {
      await superAdminApi.delete(`/super-admin/promo-codes/${p.id}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not retire the code.");
    }
  };

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Billing"
        icon={Tag}
        title="Promo"
        titleAccent="Codes"
        description="Every code in the table, active or retired. Click a code to see exactly who redeemed it and when their access runs out."
        actions={
          <button
            type="button"
            onClick={() => setForm({ ...EMPTY_FORM })}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-[filter] hover:brightness-105"
          >
            <Plus className="h-4 w-4" /> New promo code
          </button>
        }
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by code…"
          className="h-10 w-full rounded-xl border border-border/70 bg-background pl-9 pr-3 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}

      <section className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : codes.length === 0 ? (
          <div className="p-10 text-center">
            <Tag className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 font-semibold text-foreground">No promo codes</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {search ? "Nothing matches that search." : "Create one to get started."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/70 bg-muted/40 text-left text-[0.65rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Access</th>
                  <th className="px-4 py-3" title="Lifetime counter on the code — can exceed live redemptions">Uses</th>
                  <th className="px-4 py-3" title="Redemption rows that still exist">Redeemed by</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((p) => (
                  <tr key={p.id} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-3">
                        {/* The code itself opens the redemption list — that is
                            what someone reaches for instinctively. */}
                        <button
                          type="button"
                          onClick={() => void openRedemptions(p)}
                          title="Show everyone who redeemed this code"
                          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-mono font-bold text-foreground underline decoration-dotted underline-offset-4 transition-colors hover:bg-primary/10 hover:text-primary"
                        >
                          {p.code}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{p.tier_name ?? `#${p.tier_id}`}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.access_days} days</td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">
                        {p.use_count}{p.max_uses === null ? " / ∞" : ` / ${p.max_uses}`}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => void openRedemptions(p)}
                          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-semibold text-primary transition-colors hover:bg-primary/10"
                        >
                          <Users className="h-3.5 w-3.5" />
                          {p.redemption_count}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] ${
                          p.is_active
                            ? "bg-emerald-500/15 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {p.is_active ? "Active" : "Retired"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => setForm({
                              id: p.id, code: p.code, tier_id: p.tier_id, access_days: p.access_days,
                              max_uses: p.max_uses ?? 1, unlimited: p.max_uses === null, is_active: p.is_active,
                            })}
                            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {p.is_active && (
                            <button
                              type="button"
                              title="Retire (soft delete)"
                              onClick={() => void retire(p)}
                              className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-600"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs leading-6 text-muted-foreground">
        <strong className="text-foreground">Uses</strong> is a lifetime counter kept on the code.{" "}
        <strong className="text-foreground">Redeemed by</strong> counts the redemption records that still
        exist — those are removed when an account is deleted, so Uses can legitimately sit higher. Retiring a
        code never deletes anything.
      </p>

      {viewing && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setViewing(null)}
        >
          <div
            className="flex max-h-[85dvh] w-full max-w-3xl flex-col rounded-2xl border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/70 px-6 py-4">
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  Redeemed by <span className="font-mono">{viewing.code}</span>
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {viewing.tier_name ?? `Tier #${viewing.tier_id}`} · {viewing.access_days} days access ·{" "}
                  {viewing.use_count} lifetime use{viewing.use_count === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewing(null)}
                aria-label="Close"
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {!redemptions[viewing.id] ? (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading redemptions…
                </div>
              ) : redemptions[viewing.id].length === 0 ? (
                <div className="py-10 text-center">
                  <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
                  <p className="mt-3 font-semibold text-foreground">No redemption records</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                    {viewing.use_count > 0
                      ? "This code has been used, but those accounts have since been deleted — the records go with the account."
                      : "Nobody has redeemed this code yet."}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border/60">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border/60 bg-muted/40 text-left text-[0.6rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">User</th>
                        <th className="px-3 py-2">Account</th>
                        <th className="px-3 py-2">Redeemed</th>
                        <th className="px-3 py-2">Current plan</th>
                        <th className="px-3 py-2">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redemptions[viewing.id].map((r) => {
                        const exp = expiryLabel(r.expires_at);
                        return (
                          <tr key={r.id} className="border-b border-border/40 last:border-0">
                            <td className="px-3 py-2">
                              <div className="font-semibold text-foreground">
                                {`${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || "\u2014"}
                              </div>
                              <div className="text-xs text-muted-foreground">{r.email}</div>
                            </td>
                            <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                              {r.user_status ?? "\u2014"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.redeemed_at)}</td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {r.current_tier_name ?? "\u2014"}
                              {r.subscription_active === false && (
                                <span className="ml-1.5 text-[0.65rem] uppercase tracking-wide text-muted-foreground/70">
                                  inactive
                                </span>
                              )}
                            </td>
                            <td className={`px-3 py-2 font-semibold ${exp.expired ? "text-rose-600" : "text-foreground"}`}>
                              {exp.text}
                              {exp.expired && (
                                <span className="ml-1.5 text-[0.65rem] font-bold uppercase tracking-wide">expired</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="shrink-0 border-t border-border/70 px-6 py-3 text-xs leading-5 text-muted-foreground">
              Current plan and expiry are this buyer&apos;s subscription today — someone who has since
              upgraded or paid shows that plan, not the one this code granted.
            </p>
          </div>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-bold text-foreground">
                {form.id ? "Edit promo code" : "New promo code"}
              </h2>
              <button type="button" onClick={() => setForm(null)} className="rounded-lg p-1 text-muted-foreground hover:bg-accent">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Code</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="WELCOME30"
                  maxLength={64}
                  className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 font-mono text-sm uppercase focus:border-primary focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Tier</span>
                <select
                  value={form.tier_id}
                  onChange={(e) => setForm({ ...form, tier_id: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">Select a tier…</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_selectable ? "" : " (retired)"}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Access days</span>
                <input
                  type="number" min={1} max={3650}
                  value={form.access_days}
                  onChange={(e) => setForm({ ...form, access_days: Number(e.target.value) })}
                  className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
                />
              </label>

              <div>
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Max uses</span>
                <div className="mt-1.5 flex items-center gap-3">
                  <input
                    type="number" min={1}
                    value={form.unlimited ? "" : form.max_uses}
                    disabled={form.unlimited}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="h-10 w-32 rounded-xl border border-border/70 bg-background px-3 text-sm disabled:opacity-50 focus:border-primary focus:outline-none"
                  />
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={form.unlimited}
                      onChange={(e) => setForm({ ...form, unlimited: e.target.checked })}
                    />
                    Unlimited
                  </label>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active — buyers can redeem it
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setForm(null)}
                className="rounded-xl border border-border/80 px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-accent">
                Cancel
              </button>
              <button type="button" onClick={() => void save()} disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
